import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoDirective } from '@jsverse/transloco';
import { DateFilterComponent } from '../../../../../../../_shared/components';
import { EmptyStateComponent } from '../../../../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../../../../../_shared/components/states/error-state/error-state.component';
import { DateInterval } from '../../../../../../../_shared/enums';
import { DateIntervalFilterData } from '../../../../../../../_shared/models';
import { ErrorHandlerService } from '../../../../../../../_shared/services/error-handler.service';
import { AddStockComponent } from '../../add-stock/add-stock.component';
import { PartUsageByPartIdItem, PartUsageByPartIdRequest } from '../../../../models/part-usage-by-part-id.model';
import { PartsAndInventoryService } from '../../../../services/parts-and-inventory.service';

@Component({
  selector: 'app-part-usage-tab',
  standalone: true,
  imports: [CommonModule, InfiniteScrollModule, DateFilterComponent, EmptyStateComponent, ErrorStateComponent, TranslocoDirective],
  templateUrl: './part-usage-tab.component.html',
  styleUrl: './part-usage-tab.component.scss'
})
export class PartUsageTabComponent implements OnInit, OnDestroy {
  @Input() partId!: number;
  @Input() accountIds: number[] = [];
  @Input() dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;

  readonly defaultInterval = DateInterval.Month;
  readonly disabledIntervals: DateInterval[] = [DateInterval.Half];

  modalDateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;
  usageHistory: PartUsageByPartIdItem[] = [];
  isUsageLoading = false;
  isLoadingMore = false;
  hasError = false;
  hasMoreData = true;

  private currentPage = 1;
  private totalPages = 0;
  private readonly pageSize = 20;
  private cancellation = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  private readonly modalService = inject(NgbModal);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly partsAndInventoryService = inject(PartsAndInventoryService);

  ngOnInit(): void {
    if (this.dateFilter?.startDate && this.dateFilter?.endDate) {
      this.onDateFilterChange(this.dateFilter);
    }
  }

  ngOnDestroy(): void {
    this.cancellation.next();
    this.cancellation.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get shouldShowEmptyState(): boolean {
    return !this.isUsageLoading && !this.hasError && this.usageHistory.length === 0;
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.modalDateFilter = { ...filter };
    void this.resetAndLoadUsageAsync();
  }

  onScrollDown(): void {
    if (!this.hasMoreData || this.isLoadingMore || this.isUsageLoading) {
      return;
    }

    void this.loadUsagePageAsync(false);
  }

  openAddStock(): void {
    const modalRef = this.modalService.open(AddStockComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      windowClass: 'add-stock-modal-window'
    });

    modalRef.componentInstance.partId = this.partId;

    modalRef.result
      .then((result: boolean) => {
        if (result === true) {
          void this.resetAndLoadUsageAsync();
        }
      })
      .catch(() => { });
  }

  trackByUsage(item: PartUsageByPartIdItem): number {
    return item.requestId;
  }

  private async resetAndLoadUsageAsync(): Promise<void> {
    this.cancellation.next();

    this.currentPage = 1;
    this.totalPages = 0;
    this.hasMoreData = true;
    this.hasError = false;
    this.usageHistory = [];

    await this.loadUsagePageAsync(true);
  }

  private async loadUsagePageAsync(isInitialLoad: boolean): Promise<void> {
    if (isInitialLoad) {
      this.isUsageLoading = true;
      this.hasError = false;
    } else {
      this.isLoadingMore = true;
    }

    try {
      const request: PartUsageByPartIdRequest = {
        partId: this.partId,
        accountIds: this.accountIds ?? [],
        timeFrom: this.modalDateFilter.startDate!,
        timeTo: this.modalDateFilter.endDate!,
        interval: this.modalDateFilter.interval ?? this.defaultInterval,
        page: this.currentPage,
        pageSize: this.pageSize
      };

      const response = await this.partsAndInventoryService.getPartsUsageByPartIdAsync(request, this.cancellation);

      const results = response.data?.results ?? [];
      this.usageHistory = isInitialLoad ? results : [...this.usageHistory, ...results];
      this.totalPages = response.data?.totalPages ?? 0;
      this.hasMoreData = this.currentPage < this.totalPages;

      if (this.hasMoreData) {
        this.currentPage += 1;
      }

      this.isUsageLoading = false;
      this.isLoadingMore = false;
    } catch (error: any) {
      this.hasError = true;
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isUsageLoading = false;
          this.isLoadingMore = false;
        }
      });
    }
  }
}